import { useFormContext } from "react-hook-form";
import useUsStateOptions from "../hooks/useUsStateOptions";
import { classes } from "../util/style-utils";
import ZipCode from "./gdk/ZipCode";
import InputBox from "./gds/InputBox";
import StandardizeAddressModal from "./StandardizeAddressModal";
import { useState, useMemo, useContext, useEffect } from "react";
import { v4 } from "uuid";
import * as yup from 'yup';
import { ClientConfigurationContext } from "../context/ClientConfigurationContextProvider";

export const streetAddressLineOneValidationSchema = yup.string().trim()
    .matches(/^[A-Z0-9\s'\-#./&]*$/, "Street Address must contain only capitalized letters, numbers, and the following special characters:  ' - & . / and #.")
    .required("Please enter a Street Address.");
export const streetAddressLineTwoValidationSchema = yup.string()
    .matches(/^[A-Z0-9\s'\-#./&]*$/, "Street Address must contain only capitalized letters, numbers, and the following special characters:  ' - & . / and #.")
    .optional().nullable();
export const zipCodeValidationSchema = yup.string()
    .matches(/^\d{5}$/, "Zip code must be 5 digits.")
    .required("Please enter a ZIP code.");
export const cityValidationSchema = yup.string().trim()
    .matches(/^[A-Z\s-]*$/, "Please enter a valid city.")
    .required("Please enter a city.");
export const stateValidationSchema = yup.string().required("Please select a state.");
/**
 * @typedef {Object} FieldNames
 * @property {string} line1 - The field name for address line 1
 * @property {string} line2 - The field name for address line 2
 * @property {string} city - The field name for city
 * @property {string} state - The field name for state
 * @property {string} zipCode - The field name for ZIP code
 */

/**
 * @typedef {Object} Labels
 * @property {string} line1 - The label text for address line 1
 * @property {string} line2 - The label text for address line 2
 * @property {string} city - The label text for city
 * @property {string} state - The label text for state
 * @property {string} zipCode - The label text for ZIP code
 */

/**
 * @typedef {Object} AddressData
 * @property {string} street - Street address line 1
 * @property {string} street2 - Street address line 2
 * @property {string} city - City name
 * @property {string} state - State abbreviation
 * @property {string} zipCode - ZIP code
 */

/**
 * AddressFields component renders a complete address input form with standardization capabilities.
 * This component integrates with react-hook-form and provides address validation and standardization
 * through a modal interface. It includes fields for street address (line 1 & 2), city, state, and ZIP code.
 * 
 * **Important**: This component requires a FormProvider from react-hook-form to be present in the component
 * tree as it uses the useFormContext hook to access form methods and state.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {FieldNames} [props.fieldNames] - Custom field names for form integration
 * @param {Labels} [props.labels] - Custom labels for form fields
 * @returns {JSX.Element} The rendered address fields component
 * 
 * @example
 * // Basic usage with FormProvider and default field names and labels
 * import { useForm, FormProvider } from "react-hook-form";
 * 
 * function MyForm() {
 *   const methods = useForm();
 *   
 *   return (
 *     <FormProvider {...methods}>
 *       <form>
 *         <AddressFields />
 *       </form>
 *     </FormProvider>
 *   );
 * }
 * 
 * @example
 * // Custom field names and labels with FormProvider
 * import { useForm, FormProvider } from "react-hook-form";
 * 
 * function MyForm() {
 *   const methods = useForm();
 *   
 *   return (
 *     <FormProvider {...methods}>
 *       <form>
 *         <AddressFields 
 *           fieldNames={{
 *             line1: "mailingAddress1",
 *             line2: "mailingAddress2",
 *             city: "mailingCity",
 *             state: "mailingState",
 *             zipCode: "mailingZip"
 *           }}
 *           labels={{
 *             line1: "Mailing Address",
 *             line2: "Mailing Address 2",
 *             city: "Mailing City",
 *             state: "Mailing State",
 *             zipCode: "Mailing ZIP"
 *           }}
 *         />
 *       </form>
 *     </FormProvider>
 *   );
 * }
 */
export default function AddressFields({
    fieldNames = {
        line1: "addressLine1",
        line2: "addressLine2",
        city: "city",
        state: "state",
        zipCode: "zipCode"
    },
    labels = {
        line1: "Street Address",
        line2: "Street Address 2",
        city: "City",
        state: "State",
        zipCode: "ZIP Code"
    },
    country = "US"
}) {
    const [givenAddress, setGivenAddress] = useState();
    const clientConfiguration = useContext(ClientConfigurationContext);

    const stateRegions = clientConfiguration?.stateRegions || {};
    // Treat empty string as "no country selected" (for offshore), only default to "US" when undefined
    const effectiveCountry = country === "" ? "" : (country ?? "US");
    // Empty string means no country selected (offshore flow) - don't treat as US
    const isUsCountry = effectiveCountry === "US";
    const stateOptions = effectiveCountry ? stateRegions[effectiveCountry] : undefined;
    const enableAddressStandardization = isUsCountry;
    const zipCodeMaxLengthByCountry = { "US": 5, "MX": 5, "IN": 6 };
    const zipCodeMaxLength = zipCodeMaxLengthByCountry[effectiveCountry] || 9;
    const { register, getValues, setValue, formState: { errors }, control } = useFormContext();

    useEffect(() => {
        const currentZip = getValues(fieldNames.zipCode);
        if (currentZip && currentZip.length > zipCodeMaxLength) {
            setValue(fieldNames.zipCode, "");
        }
    }, [country, fieldNames.zipCode, getValues, setValue, zipCodeMaxLength]);

    // Generate a unique prefix for this component instance to avoid ID conflicts
    const idPrefix = useMemo(() => `address-${v4()}`, []);

    /**
     * Helper function to get nested error values from the errors object using dot notation.
     * 
     * @function
     * @param {string} path - Dot notation path to the error (e.g., "address.line1", "user.contact.email")
     * @returns {Object|undefined} The error object at the specified path, or undefined if not found
     * 
     * @example
     * // For errors object: { address: { line1: { message: "Required" } } }
     * const error = getNestedError("address.line1");
     * // Returns: { message: "Required" }
     * 
     * @example
     * // For non-existent path
     * const error = getNestedError("address.nonexistent");
     * // Returns: undefined
     */
    const getNestedError = (path) => {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : undefined;
        }, errors);
    };

    /**
     * Capitalizes the values of street address lines and city fields.
     * Retrieves current form values, applies capitalization, and updates the form.
     * 
     * @function
     * @returns {void}
     */
    const capitalizeValues = () => {
        const currentLine1 = getValues(fieldNames.line1);
        const currentLine2 = getValues(fieldNames.line2);
        const currentCity = getValues(fieldNames.city);

        if (currentLine1) {
            setValue(fieldNames.line1, currentLine1.toUpperCase());
        }
        if (currentLine2) {
            setValue(fieldNames.line2, currentLine2.toUpperCase());
        }
        if (currentCity) {
            setValue(fieldNames.city, currentCity.toUpperCase());
        }
    };

    /**
     * Strips unnecessary trailing spaces and punctuation from street address and city fields.
     * Removes trailing spaces, periods, commas, and hyphens from the specified fields.
     * 
     * @function
     * @returns {void}
     */
    const stripTrailingSpacesAndPunctuation = () => {
        const currentLine1 = getValues(fieldNames.line1);
        const currentLine2 = getValues(fieldNames.line2);
        const currentCity = getValues(fieldNames.city);

        const stripTrailing = (str) => {
            if (!str || str.length === 0) return str;

            const lastChar = str[str.length - 1];
            // Check if last character is not alphanumeric
            if (!/[A-Za-z0-9]/.test(lastChar)) {
                return stripTrailing(str.slice(0, -1));
            }
            return str;
        };

        if (currentLine1) {
            setValue(fieldNames.line1, stripTrailing(currentLine1));
        }
        if (currentLine2) {
            setValue(fieldNames.line2, stripTrailing(currentLine2));
        }
        if (currentCity) {
            setValue(fieldNames.city, stripTrailing(currentCity));
        }
    };
    /**
     * Handles the blur event for address input fields.
     * Captures the current form values and sets them as the given address
     * for potential address standardization.
     * 
     * @function
     * @returns {void}
     */
    const onBlur = () => {
        capitalizeValues();
        stripTrailingSpacesAndPunctuation();
        setGivenAddress({
            street: getValues(fieldNames.line1),
            street2: getValues(fieldNames.line2),
            city: getValues(fieldNames.city),
            state: getValues(fieldNames.state),
            zipCode: getValues(fieldNames.zipCode)
        });
    };

    /**
     * Handles confirmation of a standardized address from the modal.
     * Updates all address form fields with the selected standardized address values.
     * 
     * @function
     * @param {AddressData} selectedAddress - The standardized address data selected by the user
     * @returns {void}
     */
    const onAddressConfirmed = (selectedAddress) => {
        setValue(fieldNames.line1, selectedAddress.street);
        setValue(fieldNames.line2, selectedAddress.street2);
        setValue(fieldNames.city, selectedAddress.city);
        setValue(fieldNames.state, selectedAddress.state);
        setValue(fieldNames.zipCode, selectedAddress.zipCode);
    }

    return <>
        {enableAddressStandardization &&
            <StandardizeAddressModal givenAddress={givenAddress} onAddressConfirmed={onAddressConfirmed} />
        }

        <div className='flex-row'>
            <div className={classes("form-field", getNestedError(fieldNames.line1) && "form-field--error")}>
                <label htmlFor={`${idPrefix}-line-1`} className="text">
                    {labels.line1}
                </label>
                <input id={`${idPrefix}-line-1`} type="text" {...register(fieldNames.line1, { onBlur })} />
                {getNestedError(fieldNames.line1) &&
                    <span className="form-message">{getNestedError(fieldNames.line1).message}</span>
                }
            </div>

            <div className={classes("form-field", getNestedError(fieldNames.line2) && "form-field--error")}>
                <label htmlFor={`${idPrefix}-line-2`} className="text">
                    {labels.line2}
                </label>
                <input id={`${idPrefix}-line-2`} type="text" {...register(fieldNames.line2, { onBlur })} />
                {getNestedError(fieldNames.line2) &&
                    <span className="form-message">{getNestedError(fieldNames.line2).message}</span>
                }
            </div>
        </div>

        <div className='flex-row'>
            {isUsCountry ? (
                <ZipCode
                    control={control}
                    name={fieldNames.zipCode}
                    onBlur={onBlur}
                    label={labels.zipCode}
                />
            ) : (
                <div className="form-field">
                    <InputBox
                        control={control}
                        name={fieldNames.zipCode}
                        label={labels.zipCode}
                        mask={"0".repeat(zipCodeMaxLength)}
                        maskOptions={{ lazy: true }}
                    />
                </div>
            )}
        </div>

        <div className='flex-row'>
            <div className={classes("form-field", getNestedError(fieldNames.city) && "form-field--error")}>
                <label htmlFor={`${idPrefix}-city`} className="text">
                    {labels.city}
                </label>
                <input id={`${idPrefix}-city`} type="text" {...register(fieldNames.city, { onBlur })} />
                {getNestedError(fieldNames.city) &&
                    <span className="form-message">{getNestedError(fieldNames.city).message}</span>
                }
            </div>

            <div className={classes("form-field", getNestedError(fieldNames.state) && "form-field--error")}>
                <label htmlFor={`${idPrefix}-state`} className="text">
                    {labels.state}
                </label>
                <div className="select-box">
                    <select id={`${idPrefix}-state`} {...register(fieldNames.state, { onBlur })}>
                        <option value="">Please Select</option>
                        {stateOptions?.map(option => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {getNestedError(fieldNames.state) &&
                        <span className="form-message">{getNestedError(fieldNames.state).message}</span>
                    }
                </div>
            </div>
        </div>
    </>;
}
